import Print from '@mui/icons-material/Print'
import Send from '@mui/icons-material/Send'
import Download from '@mui/icons-material/Download'
import { default as EditIcon } from '@mui/icons-material/Edit'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Schedule from '@mui/icons-material/Schedule'
import { Button, CircularProgress, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import dayjs from 'dayjs'

import UploadedEmrReportImagesViewer from 'src/mentor/components/PrescriptionMakingPage/printComponents/prescriptionImageRender/UploadedPrescrptionImagesViewer'
import useTherapyReportPreviewActions from 'src/mentor/components/TherapyReportMakingPage/components/hooks/useTherapyReportPreviewActions'
import { PMPParent } from '../../PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { getBookedSlotDetails } from 'src/mentor/services/bookedSlotCache'
import {
  getInitialDataForSettingDaysOfNextVisit,
  getInitialDataForSettingExistingSelectedComplaints,
  getInitialDataForSettingExistingSelectedDiagnosis,
  getInitialDataForSettingExistingSelectedClinicalDiagnosis,
  TherapyReportBody,
  TherapyReportBodyFirstSection,
  TherapyReportFooter,
  TherapyReportHeader,
} from '../TherapyReportMakingPageComponents'
import { getInitialDataForSettingExistingSelectedTherapies } from '../../PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { getTherapyReportLabelFromEntity } from '../therapyReportLabels'

function getNextVisitSnapshotFromReport(report) {
  if (!report || typeof report !== 'object') return null
  const eventId = report?.nextVisitBookedEventId || report?.nextVisitReferenceBookedEventId || null
  if (!eventId) return null
  return {
    id: eventId,
    status: report?.nextVisitStatus || report?.nextVisitBookedEventStatus || report?.nextVisit?.status || null,
    startTime:
      report?.nextVisitStartTime ||
      report?.nextVisitBookedEventStartTime ||
      report?.nextVisit?.startTime ||
      report?.nextVisitDate ||
      null,
    endTime: report?.nextVisitEndTime || report?.nextVisitBookedEventEndTime || report?.nextVisit?.endTime || null,
    timeSlot: report?.nextVisitTimeSlot || report?.nextVisit?.timeSlot || null,
    meetingSrc: report?.nextVisitMeetingSrc || report?.nextVisitMode || report?.nextVisit?.meetingSrc || null,
    eventType: report?.nextVisitEventType || report?.nextVisit?.eventType || null,
    chamberRoomName: report?.nextVisitChamberRoomName || report?.nextVisit?.chamberRoomName || null,
    timezone: report?.nextVisitTimezone || report?.nextVisit?.timezone || null,
    eventDuration: report?.nextVisitEventDuration || report?.nextVisit?.eventDuration || null,
  }
}

function hasSufficientNextVisitSnapshot(snapshot) {
  if (!snapshot) return false
  // For preview card, status + time are the most important fields.
  return Boolean(snapshot?.status && (snapshot?.startTime || snapshot?.timeSlot))
}

const TherapyReportPreview = forwardRef(({
  report,
  hideInUI = false,
  isAlwaysPrinting = false,
  afterSaveGeneratePdfFunc = () => {},
  setReport,
  hideDownloadButton = false,
  templateData,
  /**
   * Optional heading override. When omitted, derived from `report.discipline` so a nutrition
   * report prints as "Nutrition Consultation Report" and a psychotherapy report as
   * "Therapy Report".
   */
  headingText,
  hideHeadingText = false,
  hideHeader = false,
  setIsPrintingPastHistoryReports = () => {},
  hideSendToDoctorButton = false,
  sendReportToTriggeringPractitionerApiSignature = '/api/secure/mentor/emr-reports/send-to-triggering-practitioner',
  showPageNumbers = true, // Show page numbers in preview drawer, hide in form page
  therapyDate, // Optional prop for therapy date
  onIssueReport, // Function to save report with status=completed
  bookedEventInfo, // Optional: for generating PDF filename
  preloadedNextVisitSlot = null, // Pre-fetched slot data from parent (avoids redundant network calls)
}, ref) => {
  // Ensure showPageNumbers is a boolean
  const shouldShowPageNumbers = showPageNumbers === true
  // Discipline-aware report heading. Caller's headingText prop wins; otherwise derive from
  // the report itself (its `discipline` field) so nutrition reports print correctly.
  const resolvedHeadingText = headingText ?? getTherapyReportLabelFromEntity(report)
  const targetRef = useRef()
  const reportStatusRef = useRef(report?.status) // Track report status for async checks
  const [currentPage, setCurrentPage] = useState(1)
  // Initialize totalPages to 0 - only set to > 0 when showPageNumbers is true
  const [totalPages, setTotalPages] = useState(0)
  const [nextVisitBookedSlot, setNextVisitBookedSlot] = useState(null)
  const [loadingNextVisitDetails, setLoadingNextVisitDetails] = useState(false)
  const [showDraftDialog, setShowDraftDialog] = useState(false)
  const [issuingReport, setIssuingReport] = useState(false)
  
  // Update ref when report status changes
  useEffect(() => {
    reportStatusRef.current = report?.status
  }, [report?.status])
  const {
    printTherapy,
    isPrinting,
    isPrintingActive,
    saving,
    sendReportToTriggeringPractitioner,
    sendingReport,
    saveAndGeneratePDF,
    downloadTherapyReport,
    downloadingReport,
  } = useTherapyReportPreviewActions({
    afterSaveGeneratePdfFunc,
    therapyReport: report,
    isAlwaysPrinting,
    setIsPrintingPastHistoryReports,
    therapyReportContainerRef: targetRef,
    sendReportToTriggeringPractitionerApiSignature,
    onIssueReport,
    bookedEventInfo,
  })
  
  // Expose print function via ref
  // The parent component (printReport) already checks status before calling this
  // So we can directly call printTherapy without additional checks
  useImperativeHandle(ref, () => ({
    print: () => {
      printTherapy().catch(error => {
        console.error('Error in printTherapy:', error)
        alert('An error occurred while printing. Please try again.')
      })
    },
  }))

  // Resolve next-visit slot: prefer the preloaded value from parent (zero extra requests),
  // fall back to the shared cache (one request per unique eventId across all instances).
  useEffect(() => {
    const nextVisitBookedEventId = report?.nextVisitBookedEventId || report?.nextVisitReferenceBookedEventId
    if (!nextVisitBookedEventId) return

    // If parent already resolved this slot, use it directly — no network call needed.
    if (preloadedNextVisitSlot) {
      setNextVisitBookedSlot(preloadedNextVisitSlot)
      return
    }

    const reportSnapshot = getNextVisitSnapshotFromReport(report)
    if (hasSufficientNextVisitSnapshot(reportSnapshot)) {
      setNextVisitBookedSlot(reportSnapshot)
      setLoadingNextVisitDetails(false)
      return
    }

    // Guard: already loaded for this id
    if (nextVisitBookedSlot?.id === nextVisitBookedEventId) return

    let cancelled = false
    setLoadingNextVisitDetails(true)
    getBookedSlotDetails(nextVisitBookedEventId).then((slot) => {
      if (cancelled) return
      if (slot) setNextVisitBookedSlot(slot)
      setLoadingNextVisitDetails(false)
    })
    return () => {
      cancelled = true
    }
  }, [report?.nextVisitBookedEventId, report?.nextVisitReferenceBookedEventId, preloadedNextVisitSlot])

  // Calculate page numbers for preview (only if showPageNumbers is true)
  useEffect(() => {
    // Immediately reset if showPageNumbers is false
    if (!shouldShowPageNumbers) {
      setTotalPages(0)
      return
    }
    
    // Only calculate if showPageNumbers is explicitly true
    if (shouldShowPageNumbers && targetRef.current && !isPrinting) {
      const calculatePages = () => {
        const element = targetRef.current
        if (element) {
          const pageHeight = 1123 // A4 height in pixels at 96 DPI
          const contentHeight = element.scrollHeight
          const pages = Math.ceil(contentHeight / pageHeight) || 1
          setTotalPages(pages)
        }
      }
      calculatePages()
      // Recalculate on window resize
      window.addEventListener('resize', calculatePages)
      return () => window.removeEventListener('resize', calculatePages)
    }
  }, [targetRef, isPrinting, report, shouldShowPageNumbers])

  // Debug logging removed - uncomment if needed for debugging
  // useEffect(() => {
  //   console.log('TherapyReportPreview render - showPageNumbers:', showPageNumbers, 'type:', typeof showPageNumbers, 'isPrinting:', isPrinting, 'totalPages:', totalPages)
  // }, [showPageNumbers, isPrinting, totalPages])

  function renderReport() {
    const isDraft = report?.status === 'draft'
    const hasUploadedImages = !!report?.uploadedImages && report?.uploadedImages?.length > 0
    
    return (
      <div className={`${!isPrinting ? 'hide-on-print-only' : ''} w-100`}>
        <style>
          {`
            @media print {
              /* Hide drawer and UI elements when printing */
              .MuiBackdrop-root,
              .MuiDrawer-root,
              .hide-on-print-only,
              button,
              [class*="MuiButton"],
              [class*="MuiIconButton"],
              [class*="report-drawer-header"],
              [class*="report-drawer-actions"],
              [class*="DrawerHeader"],
              [class*="DrawerTitle"],
              [class*="ActionButtonsContainer"] {
                display: none !important;
                visibility: hidden !important;
              }
              
              /* Hide floating page number indicator - target fixed position elements at bottom or with transform */
              div[style*="position: fixed"][style*="bottom"],
              div[style*="position:fixed"][style*="bottom"],
              div[style*="position: fixed"][style*="transform"],
              div[style*="position:fixed"][style*="transform"],
              /* Hide fixed position elements with high z-index (UI overlays) */
              [style*="position: fixed"][style*="z-index"],
              [style*="position:fixed"][style*="z-index"] {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                height: 0 !important;
                width: 0 !important;
                overflow: hidden !important;
              }
              
              /* Hide drawer container children except print body */
              [class*="DrawerContainer"] > *:not(.therapy-report-print-body) {
                display: none !important;
                visibility: hidden !important;
              }
              
              /* Show only the print body rectangle and its contents */
              .therapy-report-print-body {
                display: block !important;
                visibility: visible !important;
                position: relative !important;
                width: 100% !important;
                max-width: 100% !important;
                margin: 0 !important;
                padding: 0 !important;
                background: white !important;
              }
              
              .therapy-report-print-body * {
                visibility: visible !important;
              }
              
              /* Make drawer content static and full width */
              body {
                background: white !important;
                overflow: visible !important;
              }
              
              /* Hide drawer paper background but keep content */
              .MuiDrawer-paper {
                background: white !important;
                box-shadow: none !important;
                border: none !important;
                position: static !important;
              }
              
              /* A4 page settings */
              @page {
                size: A4;
                margin: 20mm;
                margin-bottom: 35mm;
              }
            }
            ${shouldShowPageNumbers ? `
            @media print {
              @page {
                margin: 20mm;
                margin-bottom: 35mm;
                @bottom-center {
                  content: "Page " counter(page) " of " counter(pages);
                  font-size: 11px;
                  color: #64748b;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                  font-weight: 500;
                }
              }
            }
            ` : ''}
            /* Always hide therapy-report-page-footer - we use @page CSS for page numbers instead */
            .therapy-report-page-footer {
              display: none !important;
              visibility: hidden !important;
            }
            @media print {
              .therapy-report-page-footer {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                height: 0 !important;
                width: 0 !important;
                overflow: hidden !important;
              }
            }
          `}
        </style>
        <PMPParent 
          hiddenInUI={hideInUI} 
          ref={targetRef} 
          className="therapy-report-print-body"
          style={{ position: 'relative', overflow: 'visible', minHeight: '100vh' }}
        >
          <div style={{ position: 'relative', zIndex: 1, backgroundColor: 'transparent' }}>
            <TherapyReportHeader
              therapyReportData={report}
              therapistName={report?.mentorName}
              templateData={templateData}
              // Pass the report status explicitly to ensure correct draft badge display
              // The header will only show the yellow badge when status === 'draft'
            />
          </div>
          {/* Report Status Card */}
          {!hideInUI && report && (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                padding: '20px 32px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderBottom: '1px solid #e2e8f0',
              }}
              className="hide-on-print-only"
            >
              <Box
                sx={{
                  maxWidth: '600px',
                  margin: '0 auto',
                  padding: '16px 20px',
                  backgroundColor: report?.status === 'completed' ? '#f0fdf4' : '#fffbeb',
                  border: `2px solid ${report?.status === 'completed' ? '#22c55e' : '#f59e0b'}`,
                  borderRadius: '12px',
                  boxShadow: report?.status === 'completed' 
                    ? '0 4px 12px rgba(34, 197, 94, 0.15)' 
                    : '0 4px 12px rgba(245, 158, 11, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {report?.status === 'completed' ? (
                  <CheckCircle sx={{ fontSize: 28, color: '#22c55e' }} />
                ) : (
                  <EditIcon sx={{ fontSize: 28, color: '#f59e0b' }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: report?.status === 'completed' ? '#15803d' : '#92400e',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {report?.status === 'completed' ? 'Report Completed' : 'Draft Report'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {(report?.updatedTime || report?.createdTime) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Schedule sx={{ fontSize: 14, color: report?.status === 'completed' ? '#16a34a' : '#b45309' }} />
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: report?.status === 'completed' ? '#16a34a' : '#b45309',
                            fontWeight: 500,
                          }}
                        >
                          {report?.updatedTime 
                            ? `Last saved: ${dayjs.utc(report.updatedTime).local().format('DD MMM YYYY, hh:mm A')}`
                            : `Created: ${dayjs.utc(report.createdTime).local().format('DD MMM YYYY, hh:mm A')}`
                          }
                        </Typography>
                      </Box>
                    )}
                    {report?.mentorName && (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: report?.status === 'completed' ? '#6b7280' : '#78716c',
                          fontWeight: 400,
                          marginLeft: (report?.updatedTime || report?.createdTime) ? '8px' : 0,
                        }}
                      >
                        • Therapist: {report.mentorName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          <div style={{ 
            flex: 1, 
            padding: '24px 32px', 
            position: 'relative', 
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
          }}>
            <TherapyReportBodyFirstSection
              therapyReportData={report}
              observationAndAdvice={{
                advice: report?.advice || null,
                observation: report?.observation || null,
              }}
              selectedComplaints={getInitialDataForSettingExistingSelectedComplaints(
                report?.complaints,
              )}
              selectedDiagnosis={getInitialDataForSettingExistingSelectedDiagnosis(
                report?.diagnosis,
              )}
              selectedPrimaryClinicalDiagnosis={(() => {
                const rawData = report?.primaryClinicalDiagnosis
                if (!rawData) {
                  return null
                }
                
                // Primary Clinical Diagnosis is a single object, not an array
                // Handle both single object and array (for backward compatibility)
                const item = Array.isArray(rawData) && rawData.length > 0 
                  ? rawData[0] 
                  : (!Array.isArray(rawData) ? rawData : null)
                
                if (!item) {
                  return null
                }
                
                // Extract title from various possible fields
                const title = item?.title || item?.label || item?.value || ''
                return {
                  ...item,
                  title: title,
                  label: title, // Ensure label exists for display
                  value: item?.value || item?.id || title
                }
              })()}
              selectedDifferentialClinicalDiagnoses={(() => {
                const rawData = report?.differentialClinicalDiagnoses
                if (!rawData) {
                  return []
                }
                
                // Always ensure we have an array with label/title for display
                if (Array.isArray(rawData) && rawData.length > 0) {
                  return rawData.map(item => {
                    // Extract title from various possible fields
                    const title = item?.title || item?.label || item?.value || ''
                    return {
                      ...item,
                      title: title,
                      label: title, // Ensure label exists for display
                      value: item?.value || item?.id || title
                    }
                  })
                }
                
                return []
              })()}
              primaryTherapyType={(() => {
                // Handle new structure (therapyType)
                if (report?.therapyType) {
                  return {
                    ...report.therapyType,
                    label: report.therapyType.title,
                    value: report.therapyType.id || report.therapyType.title,
                  }
                }
                // Legacy structure - use first from array
                const legacyTherapies = getInitialDataForSettingExistingSelectedTherapies(report?.therapyTypes)
                return legacyTherapies.length > 0 ? legacyTherapies[0] : null
              })()}
              additionalTherapyTypes={(() => {
                // Handle new structure (additionalTherapyTypes)
                if (report?.additionalTherapyTypes && report.additionalTherapyTypes.length > 0) {
                  return report.additionalTherapyTypes.map(t => ({
                    ...t,
                    label: t.title,
                    value: t.id || t.title,
                  }))
                }
                // Legacy structure - use remaining from array
                const legacyTherapies = getInitialDataForSettingExistingSelectedTherapies(report?.therapyTypes)
                return legacyTherapies.length > 1 ? legacyTherapies.slice(1) : []
              })()}
              selectedTherapyTypes={(() => {
                // Legacy fallback - combine for backward compatibility
                if (report?.therapyType) {
                  const therapyTypes = [{
                    ...report.therapyType,
                    label: report.therapyType.title,
                    value: report.therapyType.id || report.therapyType.title,
                  }]
                  if (report?.additionalTherapyTypes && report.additionalTherapyTypes.length > 0) {
                    const additional = report.additionalTherapyTypes.map(t => ({
                      ...t,
                      label: t.title,
                      value: t.id || t.title,
                    }))
                    therapyTypes.push(...additional)
                  }
                  return therapyTypes
                }
                // Legacy structure
                return getInitialDataForSettingExistingSelectedTherapies(report?.therapyTypes)
              })()}
            />
            {/* Show uploaded images if they exist */}
            {hasUploadedImages && (
              <Box sx={{ mb: 4, mt: 2 }}>
                <UploadedEmrReportImagesViewer
                  emrReportData={report}
                  setEmrReportData={setReport}
                  hiddenInUI={hideInUI}
                  dontUseSignedUrls
                />
              </Box>
            )}
            <TherapyReportBody
              observationAndAdvice={{
                advice: report?.advice || null,
                observation: report?.observation || null,
              }}
              content={report?.freeFormRichText || report?.content}
              nextVisitDate={report?.nextVisitDate}
              nextVisitInputType={report?.nextVisitDateType || 'days'}
              nextVisitInputValue={(() => {
                const value = getInitialDataForSettingDaysOfNextVisit(
                  report?.nextVisitDate,
                  report?.createdTime,
                  report?.nextVisitDateType,
                )
                return value
              })()}
              therapyDate={therapyDate || report?.createdTime || report?.therapyDate || null}
              nextVisitBookedSlot={nextVisitBookedSlot}
              loadingNextVisitDetails={loadingNextVisitDetails}
              hasNextVisitBookedEventId={!!(report?.nextVisitBookedEventId || report?.nextVisitReferenceBookedEventId)}
              clinicTimezone={(() => {
                // Get timezone from booked slot, fallback to IST
                if (nextVisitBookedSlot?.timezone) {
                  return nextVisitBookedSlot.timezone
                }
                if (nextVisitBookedSlot?.clinicLocation?.schedules?.[0]?.timezone) {
                  return nextVisitBookedSlot.clinicLocation.schedules[0].timezone
                }
                if (nextVisitBookedSlot?.clinicLocation?.timeZone?.offset) {
                  return decodeURIComponent(nextVisitBookedSlot.clinicLocation.timeZone.offset)
                }
                return '+05:30' // Default to IST
              })()}
            />
          </div>
          <TherapyReportFooter therapyReportData={report} showPageNumbers={shouldShowPageNumbers} />
        </PMPParent>
      </div>
    )
  }

  const isDraft = report?.status === 'draft'

  const handlePrintClick = () => {
    if (isDraft) {
      // Show dialog for draft reports
      setShowDraftDialog(true)
    } else {
      // Print directly for completed reports
      printTherapy().catch(error => {
        console.error('Error printing report:', error)
        alert('Failed to print report. Please try again.')
      })
    }
  }

  const handleIssueAndPrint = async () => {
    if (!onIssueReport) {
      alert('Unable to issue report. Please save the report manually first.')
      setShowDraftDialog(false)
      return
    }
    
    // Show loading state
    setIssuingReport(true)
    
    try {
      // Save with status=completed
      await onIssueReport()
      
      // Wait for the report status to update from 'draft' to 'completed'
      // Use ref to check current status (not captured closure variable)
      let retries = 0
      const maxRetries = 20 // Wait up to 4 seconds (20 * 200ms)
      while (retries < maxRetries && reportStatusRef.current === 'draft') {
        await new Promise(resolve => setTimeout(resolve, 200))
        retries++
      }
      
      // Wait a bit more for React to fully re-render with the new data
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Verify the ref is available before attempting to print
      if (!targetRef.current) {
        // Retry a few times if the ref isn't available yet
        let refRetries = 0
        const maxRefRetries = 5
        while (refRetries < maxRefRetries && !targetRef.current) {
          await new Promise(resolve => setTimeout(resolve, 200))
          refRetries++
        }
        
        if (!targetRef.current) {
          throw new Error('Report content not available. Please try refreshing the page.')
        }
      }
      
      // Close dialog only after we've verified everything is ready
      setShowDraftDialog(false)
      setIssuingReport(false)
      
      // Wait a bit more for the dialog to close and UI to settle
      await new Promise(resolve => setTimeout(resolve, 300))
      
      // Trigger print
      await printTherapy()
    } catch (error) {
      console.error('Error issuing report:', error)
      // Show the actual error message if available, otherwise show generic message
      const errorMessage = error?.message || error?.toString() || 'Failed to issue report. Please try again.'
      alert(errorMessage)
      setShowDraftDialog(false)
      setIssuingReport(false)
    }
  }

  return (
    <div className="w-100">
      {!hideHeader && (
        <div className="d-flex align-items-center gap-2 mb-2 mt-3 hide-on-print-only" style={{ opacity: 0.85 }}>
          {!hideHeadingText && <u>{resolvedHeadingText}</u>}
          {!hideDownloadButton && (
            <>
              <Button
                variant="outlined"
                startIcon={<Download />}
                sx={{ 
                  padding: '4px 12px', 
                  minWidth: '0px',
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: 'rgba(67, 56, 202, 0.3)',
                  color: 'rgba(67, 56, 202, 0.7)',
                  backgroundColor: 'rgba(67, 56, 202, 0.04)',
                  '&:hover': {
                    borderColor: 'rgba(67, 56, 202, 0.5)',
                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                    color: 'rgba(67, 56, 202, 0.9)',
                  },
                  '&:disabled': {
                    borderColor: 'rgba(67, 56, 202, 0.2)',
                    color: 'rgba(67, 56, 202, 0.4)',
                    backgroundColor: 'rgba(67, 56, 202, 0.02)',
                  },
                }}
                size="small"
                disabled={downloadingReport}
                onClick={() => {
                  downloadTherapyReport()
                }}
              >
                Download PDF {downloadingReport && <CircularProgress size="14px" sx={{ ml: 1 }} />}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Print />}
                sx={{ 
                  padding: '4px 12px', 
                  minWidth: '0px',
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: 'rgba(67, 56, 202, 0.3)',
                  color: 'rgba(67, 56, 202, 0.7)',
                  backgroundColor: 'rgba(67, 56, 202, 0.04)',
                  '&:hover': {
                    borderColor: 'rgba(67, 56, 202, 0.5)',
                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                    color: 'rgba(67, 56, 202, 0.9)',
                  },
                }}
                size="small"
                disabled={isPrintingActive || saving}
                onClick={handlePrintClick}
              >
                Print {(saving || isPrintingActive) && <CircularProgress size="14px" sx={{ ml: 1 }} />}
              </Button>
            </>
          )}
          {!hideSendToDoctorButton && (
            <Button
              variant="outlined"
              startIcon={<Send />}
              sx={{ 
                padding: '4px 12px', 
                minWidth: '0px',
                fontSize: '12px',
                textTransform: 'none',
                borderColor: 'rgba(67, 56, 202, 0.3)',
                color: 'rgba(67, 56, 202, 0.7)',
                backgroundColor: 'rgba(67, 56, 202, 0.04)',
                '&:hover': {
                  borderColor: 'rgba(67, 56, 202, 0.5)',
                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                  color: 'rgba(67, 56, 202, 0.9)',
                },
                '&:disabled': {
                  borderColor: 'rgba(67, 56, 202, 0.2)',
                  color: 'rgba(67, 56, 202, 0.4)',
                  backgroundColor: 'rgba(67, 56, 202, 0.02)',
                },
              }}
              size="small"
              disabled={sendingReport}
              onClick={() => {
                sendReportToTriggeringPractitioner()
              }}
            >
              Send To {report?.requestingDoctorName || 'Consulting Doctor'}{' '}
              {sendingReport && <CircularProgress size="14px" sx={{ ml: 1 }} />}
            </Button>
          )}
        </div>
      )}
      {renderReport()}
      
      {/* Draft Report Dialog */}
      <Dialog
        open={showDraftDialog}
        onClose={issuingReport ? undefined : () => setShowDraftDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px',
          },
        }}
      >
        {issuingReport ? (
          // Loading state
          <>
            <DialogContent sx={{ padding: '40px 24px', textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                <CircularProgress
                  size={60}
                  thickness={4}
                  sx={{
                    color: 'rgba(245, 158, 11, 0.9)',
                  }}
                />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(51, 51, 51, 0.9)',
                      fontSize: '18px',
                      fontWeight: 600,
                      marginBottom: '8px',
                    }}
                  >
                    Issuing the report...
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(100, 116, 139, 0.9)',
                      fontSize: '14px',
                    }}
                  >
                    Please wait while we mark the report as completed
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
          </>
        ) : (
          // Normal state
          <>
            <DialogTitle
              sx={{
                padding: '20px 24px 16px',
                fontSize: '18px',
                fontWeight: 600,
                color: 'rgba(146, 64, 14, 0.9)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircle sx={{ color: 'rgba(245, 158, 11, 0.9)', fontSize: '24px' }} />
              </Box>
              Draft Report
            </DialogTitle>
            <DialogContent sx={{ padding: '20px 24px' }}>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(51, 51, 51, 0.9)',
                  fontSize: '15px',
                  lineHeight: 1.6,
                  marginBottom: '8px',
                }}
              >
                This is a draft version of the therapy report.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(100, 116, 139, 0.9)',
                  fontSize: '14px',
                  lineHeight: 1.6,
                }}
              >
                Please issue the report first before printing. This will mark the report as completed and allow you to print it.
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                padding: '16px 24px 20px',
                gap: '12px',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                onClick={() => setShowDraftDialog(false)}
                variant="outlined"
                sx={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  textTransform: 'none',
                  borderRadius: '10px',
                  borderColor: 'rgba(100, 116, 139, 0.3)',
                  color: 'rgba(100, 116, 139, 0.9)',
                  '&:hover': {
                    borderColor: 'rgba(100, 116, 139, 0.5)',
                    backgroundColor: 'rgba(100, 116, 139, 0.04)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleIssueAndPrint}
                variant="contained"
                disabled={false}
                startIcon={<CheckCircle />}
                sx={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  textTransform: 'none',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(245, 158, 11, 0.9)',
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(245, 158, 11, 1)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(245, 158, 11, 0.5)',
                  },
                }}
              >
                Issue & Print
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  )
})

TherapyReportPreview.displayName = 'TherapyReportPreview'

export default TherapyReportPreview
